import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFlask, faPlus, faTimes, faPlay, faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons'

import GlobalStyles from '../../../components/component-items/styles'
import { globalState } from '../../../store'

// Types
type OrderLine = {
  sku: string
  quantity: number
}

type WarehouseInventory = {
  id: string
  name: string
  available: Record<string, number>
  cost: number
  isPrimary: boolean
}

type SimulationResult = {
  handling_units: Array<{
    warehouse_id: string
    lines: Record<string, number>
    is_primary: boolean
  }>
  backorders: Record<string, Record<string, number>>
  cancellations: Record<string, Record<string, number>>
  is_split: boolean
  primary_site: string
}

type PolicyConfig = {
  allowPartial: boolean
  allowLinePartial: boolean
  shortageBehavior: 'BACKORDER' | 'CANCEL'
  lineShortageBehavior: 'BACKORDER' | 'CANCEL'
  selectionStrategy: 'PRIMARY_SITE_ONLY' | 'BEST_SINGLE_SITE' | 'BEST_MULTI_SITE'
}

type BusinessOptionKey =
  | 'SHIP_PARTIAL_BACKORDER'
  | 'SHIP_COMPLETE'
  | 'SHORT_SHIP_CANCEL'
  | 'FILL_ORDER_CANCEL'
  | 'FILL_LINE_CANCEL'
  | 'CUSTOM'

type BusinessOption = {
  key: BusinessOptionKey
  name: string
  config: PolicyConfig
}

const BUSINESS_OPTIONS: BusinessOption[] = [
  {
    key: 'SHIP_COMPLETE',
    name: 'Ship Complete',
    config: {
      allowPartial: false,
      allowLinePartial: false,
      shortageBehavior: 'BACKORDER',
      lineShortageBehavior: 'BACKORDER',
      selectionStrategy: 'PRIMARY_SITE_ONLY',
    },
  },
  {
    key: 'FILL_ORDER_CANCEL',
    name: 'Fill Entire Order or Cancel Entire Order',
    config: {
      allowPartial: false,
      allowLinePartial: false,
      shortageBehavior: 'CANCEL',
      lineShortageBehavior: 'CANCEL',
      selectionStrategy: 'PRIMARY_SITE_ONLY',
    },
  },
  {
    key: 'SHIP_PARTIAL_BACKORDER',
    name: 'Ship Partial and Backorder Remaining',
    config: {
      allowPartial: true,
      allowLinePartial: false,
      shortageBehavior: 'BACKORDER',
      lineShortageBehavior: 'BACKORDER',
      selectionStrategy: 'BEST_MULTI_SITE',
    },
  },
  {
    key: 'SHORT_SHIP_CANCEL',
    name: 'Short Ship and Cancel Remaining',
    config: {
      allowPartial: true,
      allowLinePartial: true,
      shortageBehavior: 'CANCEL',
      lineShortageBehavior: 'CANCEL',
      selectionStrategy: 'BEST_MULTI_SITE',
    },
  },
  {
    key: 'FILL_LINE_CANCEL',
    name: 'Fill Entire Line or Cancel Entire Line',
    config: {
      allowPartial: true,
      allowLinePartial: false,
      shortageBehavior: 'BACKORDER',
      lineShortageBehavior: 'CANCEL',
      selectionStrategy: 'BEST_SINGLE_SITE',
    },
  },
  {
    key: 'CUSTOM',
    name: 'Custom',
    config: {
      allowPartial: true,
      allowLinePartial: true,
      shortageBehavior: 'BACKORDER',
      lineShortageBehavior: 'BACKORDER',
      selectionStrategy: 'BEST_MULTI_SITE',
    },
  },
]

const resolveOptionKeyFromPolicy = (policy: PolicyConfig): BusinessOptionKey => {
  const match = BUSINESS_OPTIONS.find(
    (option) =>
      option.key !== 'CUSTOM' &&
      policy.allowPartial === option.config.allowPartial &&
      policy.shortageBehavior === option.config.shortageBehavior &&
      policy.allowLinePartial === option.config.allowLinePartial &&
      policy.lineShortageBehavior === option.config.lineShortageBehavior &&
      policy.selectionStrategy === option.config.selectionStrategy
  )
  return match ? match.key : 'CUSTOM'
}

// Styled Components
const SimulatorCard = styled.div`
  background: ${(props) => props.theme.colors.white};
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(82, 63, 105, 0.12);
  overflow: hidden;
`

const SimulatorHeader = styled.button`
  width: 100%;
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  cursor: pointer;
  color: white;

  h2 {
    font-size: 1.25rem;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .toggle-icon {
    font-size: 1rem;
  }
`

const SimulatorBody = styled.div<{ $expanded: boolean }>`
  display: ${(props) => (props.$expanded ? 'block' : 'none')};
  padding: 1.5rem;
`

const Section = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;

  h3 {
    font-size: 1rem;
    margin: 0;
    color: ${(props) => props.theme.colors.mulledWine};
  }
`

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  th,
  td {
    padding: 0.5rem;
    border: 1px solid ${(props) => props.theme.colors.gallery};
    text-align: left;
  }

  th {
    background: ${(props) => props.theme.colors.whiteLilac};
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  input {
    width: 100%;
    border: 1px solid ${(props) => props.theme.colors.gallery};
    border-radius: 4px;
    padding: 0.35rem 0.5rem;
    font-size: 0.875rem;

    &:focus {
      border-color: ${(props) => props.theme.colors.java};
      outline: none;
    }
  }

  input[type='number'] {
    width: 80px;
  }

  input[type='checkbox'] {
    width: auto;
  }
`

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.65rem;
  border: 1px dashed ${(props) => props.theme.colors.gallery};
  border-radius: 4px;
  background: transparent;
  color: ${(props) => props.theme.colors.gray};
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) => props.theme.colors.java};
    color: ${(props) => props.theme.colors.java};
  }
`

const RemoveButton = styled.button`
  border: none;
  background: transparent;
  color: ${(props) => props.theme.colors.gray};
  cursor: pointer;
  padding: 0.25rem;

  &:hover {
    color: ${(props) => props.theme.colors.crimson};
  }
`

const PolicyConfigGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;

  label {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(props) => props.theme.colors.gray};
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  select {
    border: 1px solid ${(props) => props.theme.colors.gallery};
    border-radius: 4px;
    padding: 0.45rem 0.5rem;
    font-size: 0.875rem;
  }
`

const PresetSelector = styled.div`
  margin-bottom: 1rem;

  label {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(props) => props.theme.colors.gray};
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  select {
    border: 1px solid ${(props) => props.theme.colors.gallery};
    border-radius: 4px;
    padding: 0.55rem 0.65rem;
    font-size: 0.95rem;
    max-width: 350px;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);

    &:focus {
      border-color: ${(props) => props.theme.colors.java};
      outline: none;
    }
  }
`

const RunButton = styled(GlobalStyles.Button)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;

  &:hover {
    opacity: 0.9;
  }
`

const ResultsSection = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 2px solid ${(props) => props.theme.colors.gallery};
`

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const ResultCard = styled.div<{ $variant: 'ship' | 'backorder' | 'cancel' }>`
  border-radius: 8px;
  padding: 1rem;
  background: ${({ $variant }) => {
    if ($variant === 'ship') return 'rgba(29, 201, 183, 0.08)'
    if ($variant === 'backorder') return 'rgba(255, 193, 7, 0.08)'
    return 'rgba(220, 53, 69, 0.08)'
  }};
  border-left: 4px solid
    ${({ $variant }) => {
      if ($variant === 'ship') return '#1dc9b7'
      if ($variant === 'backorder') return '#ffc107'
      return '#dc3545'
    }};

  h4 {
    margin: 0 0 0.75rem 0;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${({ $variant }) => {
      if ($variant === 'ship') return '#1dc9b7'
      if ($variant === 'backorder') return '#d39e00'
      return '#dc3545'
    }};
  }
`

const ResultItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.35rem 0;
  font-size: 0.875rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  &:last-child {
    border-bottom: none;
  }

  .warehouse {
    font-weight: 600;
    color: ${(props) => props.theme.colors.mulledWine};
  }

  .sku {
    color: ${(props) => props.theme.colors.gray};
  }

  .quantity {
    font-weight: 600;
  }
`

const SplitBadge = styled.span<{ $isSplit: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${(props) => (props.$isSplit ? 'rgba(102, 126, 234, 0.15)' : 'rgba(29, 201, 183, 0.15)')};
  color: ${(props) => (props.$isSplit ? '#667eea' : '#1dc9b7')};
`

const EmptyResult = styled.div`
  color: ${(props) => props.theme.colors.gray};
  font-size: 0.875rem;
  font-style: italic;
`

const ErrorMessage = styled.div`
  padding: 1rem;
  background: rgba(220, 53, 69, 0.1);
  border-radius: 4px;
  color: #dc3545;
  font-size: 0.9rem;
  margin-top: 1rem;
`

// Default data
const DEFAULT_ORDER_LINES: OrderLine[] = [{ sku: 'BANANA', quantity: 10 }]

const DEFAULT_WAREHOUSES: WarehouseInventory[] = [
  { id: 'fontana', name: 'Fontana', available: { BANANA: 5 }, cost: 1, isPrimary: true },
  { id: 'columbus', name: 'Columbus', available: { BANANA: 3 }, cost: 2, isPrimary: false },
  { id: 'dallas', name: 'Dallas', available: { BANANA: 5 }, cost: 3, isPrimary: false },
]

const DEFAULT_POLICY: PolicyConfig = {
  allowPartial: true,
  allowLinePartial: true,
  shortageBehavior: 'BACKORDER',
  lineShortageBehavior: 'BACKORDER',
  selectionStrategy: 'BEST_MULTI_SITE',
}

// Component
export const StrategySimulator: React.FC = () => {
  const {
    state: { csrf },
  } = globalState()

  const [expanded, setExpanded] = useState(false)
  const [orderLines, setOrderLines] = useState<OrderLine[]>(DEFAULT_ORDER_LINES)
  const [warehouses, setWarehouses] = useState<WarehouseInventory[]>(DEFAULT_WAREHOUSES)
  const [policy, setPolicy] = useState<PolicyConfig>(DEFAULT_POLICY)
  const [selectedPreset, setSelectedPreset] = useState<BusinessOptionKey>(() =>
    resolveOptionKeyFromPolicy(DEFAULT_POLICY)
  )
  const [result, setResult] = useState<SimulationResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // Handle preset selection
  const handlePresetChange = (presetKey: BusinessOptionKey) => {
    setSelectedPreset(presetKey)
    const option = BUSINESS_OPTIONS.find((opt) => opt.key === presetKey)
    if (option) {
      setPolicy(option.config)
    }
  }

  // Update policy with auto-reset logic
  const updatePolicy = (patch: Partial<PolicyConfig>) => {
    setPolicy((prev) => {
      const next = { ...prev, ...patch }

      // Auto-reset logic when allowPartial changes to false
      if ('allowPartial' in patch && patch.allowPartial === false) {
        // Reset allowLinePartial since it requires allowPartial
        next.allowLinePartial = false
        // Reset lineShortageBehavior to default
        next.lineShortageBehavior = 'BACKORDER'
        // Reset strategy if it was BEST_MULTI_SITE (requires allowPartial)
        if (next.selectionStrategy === 'BEST_MULTI_SITE') {
          next.selectionStrategy = 'PRIMARY_SITE_ONLY'
        }
      }

      // Auto-reset when allowLinePartial changes to false
      if ('allowLinePartial' in patch && patch.allowLinePartial === false) {
        next.lineShortageBehavior = 'BACKORDER'
      }

      // Update selected preset based on new policy config
      setSelectedPreset(resolveOptionKeyFromPolicy(next))

      return next
    })
  }

  // Check warehouse count for strategy options
  const warehouseCount = warehouses.length

  // Get all unique SKUs across order lines
  const allSkus = useMemo(() => {
    const skus = new Set<string>()
    orderLines.forEach((line) => {
      if (line.sku) skus.add(line.sku)
    })
    return Array.from(skus)
  }, [orderLines])

  // Order line handlers
  const addOrderLine = () => {
    setOrderLines([...orderLines, { sku: '', quantity: 1 }])
  }

  const updateOrderLine = (index: number, field: keyof OrderLine, value: string | number) => {
    const updated = [...orderLines]
    if (field === 'quantity') {
      updated[index][field] = Number(value)
    } else {
      updated[index][field] = String(value)
    }
    setOrderLines(updated)
  }

  const removeOrderLine = (index: number) => {
    if (orderLines.length > 1) {
      setOrderLines(orderLines.filter((_, i) => i !== index))
    }
  }

  // Warehouse handlers
  const addWarehouse = () => {
    const inventory: Record<string, number> = {}
    allSkus.forEach((sku) => (inventory[sku] = 0))
    setWarehouses([
      ...warehouses,
      {
        id: `warehouse-${Date.now()}`,
        name: '',
        available: inventory,
        cost: warehouses.length + 1,
        isPrimary: false,
      },
    ])
  }

  const updateWarehouse = (index: number, field: string, value: any) => {
    setWarehouses((prev) => {
      const isPrimaryField = field === 'isPrimary'
      const isMakingPrimary = isPrimaryField && Boolean(value)

      return prev.map((wh, i) => {
        // Handle isPrimary updates, ensuring only one primary at a time
        if (isPrimaryField) {
          if (isMakingPrimary) {
            if (i === index) return { ...wh, isPrimary: true }
            return wh.isPrimary ? { ...wh, isPrimary: false } : wh
          }
          if (i === index) return { ...wh, isPrimary: false }
          return wh
        }

        // Other fields only affect the selected warehouse
        if (i !== index) return wh

        if (field.startsWith('available.')) {
          const sku = field.replace('available.', '')
          return { ...wh, available: { ...wh.available, [sku]: Number(value) } }
        }
        if (field === 'cost') return { ...wh, cost: Number(value) }
        return { ...wh, [field]: value } as WarehouseInventory
      })
    })
  }

  const removeWarehouse = (index: number) => {
    if (warehouses.length > 1) {
      const removed = warehouses[index]
      let updated = warehouses.filter((_, i) => i !== index)
      // If we removed the primary, make the first one primary
      if (removed.isPrimary && updated.length > 0) {
        updated = updated.map((wh, i) => (i === 0 ? { ...wh, isPrimary: true } : wh))
      }
      setWarehouses(updated)
      // Auto-reset strategy if warehouses drop below 2
      if (updated.length < 2) {
        if (policy.selectionStrategy === 'BEST_MULTI_SITE' || policy.selectionStrategy === 'BEST_SINGLE_SITE') {
          setPolicy((prev) => ({ ...prev, selectionStrategy: 'PRIMARY_SITE_ONLY' }))
        }
      }
    }
  }

  // Ensure all warehouses have all SKUs in their available map
  const normalizedWarehouses = useMemo(() => {
    return warehouses.map((wh) => {
      const available = { ...wh.available }
      allSkus.forEach((sku) => {
        if (!(sku in available)) {
          available[sku] = 0
        }
      })
      return { ...wh, available }
    })
  }, [warehouses, allSkus])

  // Run simulation
  const runSimulation = async () => {
    setLoading(true)
    setError(null)

    const primaryWarehouse = normalizedWarehouses.find((wh) => wh.isPrimary)
    if (!primaryWarehouse) {
      setError('Please select a primary warehouse')
      setLoading(false)
      return
    }

    const payload = {
      policy: {
        allow_partial: policy.allowPartial,
        allow_line_partial: policy.allowLinePartial,
        shortage_behavior: policy.shortageBehavior,
        line_shortage_behavior: policy.lineShortageBehavior,
        selection_strategy: policy.selectionStrategy,
      },
      order_lines: orderLines
        .filter((line) => line.sku && line.quantity > 0)
        .map((line) => ({
          sku: line.sku,
          quantity: line.quantity,
        })),
      warehouses: normalizedWarehouses.map((wh) => ({
        id: wh.name || wh.id,
        available: wh.available,
        cost: wh.cost,
        is_primary: wh.isPrimary,
      })),
    }

    try {
      const response = await fetch('/api/order/fulfillment-policies/simulate/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrf || '',
        },
        body: JSON.stringify(payload),
      })

      const data = await response.json()

      if (!response.ok) {
        setError(data.error || 'Simulation failed')
        setResult(null)
      } else {
        setResult(data)
        setError(null)
      }
    } catch (err: any) {
      setError(err.message || 'Network error')
      setResult(null)
    } finally {
      setLoading(false)
    }
  }

  // Render handling unit results
  const renderHandlingUnits = () => {
    if (!result?.handling_units?.length) {
      return <EmptyResult>No handling units</EmptyResult>
    }

    return result.handling_units.map((handlingUnit, idx) => (
      <div key={idx}>
        {Object.entries(handlingUnit.lines).map(([sku, qty]) => (
          <ResultItem key={`${handlingUnit.warehouse_id}-${sku}`}>
            <span>
              <span className="warehouse">{handlingUnit.warehouse_id}</span>
              {handlingUnit.is_primary && ' (Primary)'}
            </span>
            <span>
              <span className="sku">{sku}</span>: <span className="quantity">{qty}</span>
            </span>
          </ResultItem>
        ))}
      </div>
    ))
  }

  // Render backorders
  const renderBackorders = () => {
    if (!result?.backorders || Object.keys(result.backorders).length === 0) {
      return <EmptyResult>No backorders</EmptyResult>
    }

    return Object.entries(result.backorders).map(([warehouseId, lines]) =>
      Object.entries(lines).map(([sku, qty]) => (
        <ResultItem key={`${warehouseId}-${sku}`}>
          <span className="warehouse">{warehouseId}</span>
          <span>
            <span className="sku">{sku}</span>: <span className="quantity">{qty}</span>
          </span>
        </ResultItem>
      ))
    )
  }

  // Render cancellations
  const renderCancellations = () => {
    if (!result?.cancellations || Object.keys(result.cancellations).length === 0) {
      return <EmptyResult>No cancellations</EmptyResult>
    }

    return Object.entries(result.cancellations).map(([warehouseId, lines]) =>
      Object.entries(lines).map(([sku, qty]) => (
        <ResultItem key={`${warehouseId}-${sku}`}>
          <span className="warehouse">{warehouseId}</span>
          <span>
            <span className="sku">{sku}</span>: <span className="quantity">{qty}</span>
          </span>
        </ResultItem>
      ))
    )
  }

  return (
    <SimulatorCard>
      <SimulatorHeader onClick={() => setExpanded(!expanded)}>
        <h2>
          <FontAwesomeIcon icon={faFlask} />
          Strategy Simulator
        </h2>
        <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} className="toggle-icon" />
      </SimulatorHeader>

      <SimulatorBody $expanded={expanded}>
        {/* Policy Configuration */}
        <Section>
          <SectionHeader>
            <h3>Policy Configuration</h3>
          </SectionHeader>
          <PresetSelector>
            <label>
              Quick Preset
              <select value={selectedPreset} onChange={(e) => handlePresetChange(e.target.value as BusinessOptionKey)}>
                {BUSINESS_OPTIONS.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.name}
                  </option>
                ))}
              </select>
            </label>
          </PresetSelector>
          <PolicyConfigGrid>
            {/* 1. Allow Partial Handling Units */}
            <label>
              Allow Partial Handling Units
              <select
                value={policy.allowPartial ? 'yes' : 'no'}
                onChange={(e) => updatePolicy({ allowPartial: e.target.value === 'yes' })}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
            {/* 2. Allow Line Partial (conditional - only if allowPartial) */}
            {policy.allowPartial && (
              <label>
                Allow Line Partial
                <select
                  value={policy.allowLinePartial ? 'yes' : 'no'}
                  onChange={(e) => updatePolicy({ allowLinePartial: e.target.value === 'yes' })}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>
            )}
            {/* 3. Selection Strategy (with filtered options) */}
            <label>
              Selection Strategy
              <select
                value={policy.selectionStrategy}
                onChange={(e) => updatePolicy({ selectionStrategy: e.target.value as any })}
              >
                <option value="PRIMARY_SITE_ONLY">Primary Site Only</option>
                {warehouseCount >= 2 && <option value="BEST_SINGLE_SITE">Best Single Site</option>}
                {warehouseCount >= 2 && policy.allowPartial && <option value="BEST_MULTI_SITE">Best Multi Site</option>}
              </select>
            </label>
            {/* 4. Shortage Behavior */}
            <label>
              Shortage Behavior
              <select
                value={policy.shortageBehavior}
                onChange={(e) => updatePolicy({ shortageBehavior: e.target.value as any })}
              >
                <option value="BACKORDER">Backorder</option>
                <option value="CANCEL">Cancel</option>
              </select>
            </label>
            {/* 5. Line Shortage Behavior (conditional - only if allowLinePartial) */}
            {policy.allowLinePartial && (
              <label>
                Line Shortage Behavior
                <select
                  value={policy.lineShortageBehavior}
                  onChange={(e) => updatePolicy({ lineShortageBehavior: e.target.value as any })}
                >
                  <option value="BACKORDER">Backorder</option>
                  <option value="CANCEL">Cancel</option>
                </select>
              </label>
            )}
          </PolicyConfigGrid>
        </Section>

        {/* Order Lines */}
        <Section>
          <SectionHeader>
            <h3>Order Lines</h3>
            <AddButton type="button" onClick={addOrderLine}>
              <FontAwesomeIcon icon={faPlus} /> Add SKU
            </AddButton>
          </SectionHeader>
          <DataTable>
            <thead>
              <tr>
                <th>SKU</th>
                <th>Quantity</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {orderLines.map((line, idx) => (
                <tr key={idx}>
                  <td>
                    <input
                      type="text"
                      value={line.sku}
                      onChange={(e) => updateOrderLine(idx, 'sku', e.target.value)}
                      placeholder="e.g. BANANA"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="1"
                      value={line.quantity}
                      onChange={(e) => updateOrderLine(idx, 'quantity', e.target.value)}
                    />
                  </td>
                  <td>
                    <RemoveButton type="button" onClick={() => removeOrderLine(idx)} disabled={orderLines.length === 1}>
                      <FontAwesomeIcon icon={faTimes} />
                    </RemoveButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </Section>

        {/* Warehouses */}
        <Section>
          <SectionHeader>
            <h3>Warehouse Inventory</h3>
            <AddButton type="button" onClick={addWarehouse}>
              <FontAwesomeIcon icon={faPlus} /> Add Warehouse
            </AddButton>
          </SectionHeader>
          <DataTable>
            <thead>
              <tr>
                <th>Warehouse</th>
                <th>Cost</th>
                <th>Primary</th>
                {allSkus.map((sku) => (
                  <th key={sku}>{sku}</th>
                ))}
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {normalizedWarehouses.map((wh, idx) => (
                <tr key={wh.id}>
                  <td>
                    <input
                      type="text"
                      value={wh.name}
                      onChange={(e) => updateWarehouse(idx, 'name', e.target.value)}
                      placeholder="e.g. Fontana"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      value={wh.cost}
                      onChange={(e) => updateWarehouse(idx, 'cost', e.target.value)}
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={wh.isPrimary}
                      onChange={(e) => updateWarehouse(idx, 'isPrimary', e.target.checked)}
                    />
                  </td>
                  {allSkus.map((sku) => (
                    <td key={sku}>
                      <input
                        type="number"
                        min="0"
                        value={wh.available[sku] || 0}
                        onChange={(e) => updateWarehouse(idx, `available.${sku}`, e.target.value)}
                      />
                    </td>
                  ))}
                  <td>
                    <RemoveButton type="button" onClick={() => removeWarehouse(idx)} disabled={warehouses.length === 1}>
                      <FontAwesomeIcon icon={faTimes} />
                    </RemoveButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </Section>

        {/* Run Button */}
        <RunButton type="button" onClick={runSimulation} disabled={loading}>
          <FontAwesomeIcon icon={faPlay} />
          {loading ? 'Running...' : 'Run Simulation'}
        </RunButton>

        {/* Error */}
        {error && <ErrorMessage>{error}</ErrorMessage>}

        {/* Results */}
        {result && (
          <ResultsSection>
            <SectionHeader>
              <h3>Results</h3>
              <SplitBadge $isSplit={result.is_split}>
                {result.is_split ? 'Split Order' : 'Single Handling Unit'}
              </SplitBadge>
            </SectionHeader>
            <ResultsGrid>
              <ResultCard $variant="ship">
                <h4>Handling Units</h4>
                {renderHandlingUnits()}
              </ResultCard>
              <ResultCard $variant="backorder">
                <h4>Backorders</h4>
                {renderBackorders()}
              </ResultCard>
              <ResultCard $variant="cancel">
                <h4>Cancellations</h4>
                {renderCancellations()}
              </ResultCard>
            </ResultsGrid>
          </ResultsSection>
        )}
      </SimulatorBody>
    </SimulatorCard>
  )
}

export default StrategySimulator
